import { useEffect, useMemo } from 'react';
import { BiCheckCircle, BiChevronRight, BiErrorCircle, BiWrench } from 'react-icons/bi';
import { useNavigate } from 'react-router-dom';

import { Skeleton } from '@/components/Skeleton';
import { StatusPill, type StatusTone } from '@/components/StatusPill/StatusPill';
import { isTaskExpired } from '@/components/TaskExpiringLabel/taskExpiration';
import { usePersonsMap } from '@/hooks/usePersonsMap';
import { TaskStatus, type TaskResponse } from '@/store/tasks/tasks.dto';
import { useTasks } from '@/store/tasks/tasks.store';
import SingleTaskModal from '@pages/MaintenancePage/MaintanenceTaskModal';
import { OVERDUE_PARAM } from '@pages/MaintenancePage/taskFilters';
import { useTaskidToShow } from '@pages/MaintenancePage/use-task-modal';

import { BaseWidget } from '../BaseWidget';

import s from './NeedsAttentionWidget.module.scss';

const SCHEDULE_ROUTE = '/maintain/schedule';
// The row counts with the same predicate the list filters on, so the number here and
// the rows behind it agree.
const OVERDUE_ROUTE = `${SCHEDULE_ROUTE}?${OVERDUE_PARAM}=1`;
const VISIBLE_ITEMS = 3;
const DAY_MS = 24 * 60 * 60 * 1000;

const CLOSED_STATUSES = new Set<string>([TaskStatus.COMPLETED, TaskStatus.CANCELED]);

const deadlineOf = (task: TaskResponse): number => {
  const raw = task.deadline || task.time_slot?.end || task.time_slot?.start;
  const time = raw ? new Date(raw).getTime() : NaN;
  return Number.isNaN(time) ? Infinity : time;
};

const describe = (task: TaskResponse, now: number) => {
  const deadline = deadlineOf(task);
  if (deadline === Infinity) {
    return { pill: 'No due date', tone: 'neutral' as StatusTone, detail: task.equipment?.name };
  }
  const days = Math.round((deadline - now) / DAY_MS);
  if (days < 0) {
    return {
      pill: 'Overdue',
      tone: 'danger' as StatusTone,
      detail: [`${Math.abs(days)} d overdue`, task.equipment?.name].filter(Boolean).join(' · '),
    };
  }
  return {
    pill: days === 0 ? 'Today' : `${days} d`,
    tone: (days <= 7 ? 'warning' : 'neutral') as StatusTone,
    detail: [days === 0 ? 'Due today' : `${days} d left`, task.equipment?.name]
      .filter(Boolean)
      .join(' · '),
  };
};

export const NeedsAttentionWidget = () => {
  const navigate = useNavigate();
  const personsMap = usePersonsMap();
  const { tasksList, getAllTasks, isLoading } = useTasks();
  const [taskToShowId, setTaskToShowId] = useTaskidToShow();
  useEffect(() => {
    void getAllTasks();
  }, [getAllTasks]);

  const now = new Date().getTime();

  const openTasks = useMemo(
    () =>
      (tasksList?.items ?? [])
        .filter((task) => task.equipment && !CLOSED_STATUSES.has(task.status))
        .sort((a, b) => deadlineOf(a) - deadlineOf(b)),
    [tasksList],
  );

  const overdueCount = openTasks.filter(isTaskExpired).length;
  // Same split as the overdue row above, so nothing falls between the banner and the rows.
  const visible = openTasks.filter((task) => !isTaskExpired(task)).slice(0, VISIBLE_ITEMS);

  // Rides alongside every branch: completing a task from the modal can empty the list
  // out from under it, and the modal has to stay up to be closed.
  const taskModal = taskToShowId ? (
    <SingleTaskModal
      id={taskToShowId}
      onClose={() => setTaskToShowId(null)}
      personsMap={personsMap}
    />
  ) : null;

  if (isLoading && !tasksList) {
    return (
      <BaseWidget headerTitle="Needs Attention" headerIcon={<BiErrorCircle />}>
        {/* Mirrors the task rows below so the card keeps its shape when they land. */}
        <ul className={s.list} aria-hidden="true">
          {Array.from({ length: VISIBLE_ITEMS }, (_, i) => (
            <li key={i} className={s.item}>
              <div className={`${s.row} ${s.skeletonRow}`}>
                <Skeleton variant="block" className={s.iconBone} />
                <span className={s.rowText}>
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="40%" />
                </span>
                <Skeleton variant="block" className={s.pillBone} />
                <BiChevronRight className={s.chevron} />
              </div>
            </li>
          ))}
        </ul>
      </BaseWidget>
    );
  }

  if (openTasks.length === 0) {
    return (
      <>
        <BaseWidget headerTitle="Needs Attention" headerIcon={<BiErrorCircle />}>
          <div className={s.empty}>
            <span className={s.rowIcon} aria-hidden="true">
              <BiCheckCircle />
            </span>
            <span className={s.rowText}>
              <span className={s.rowLabel}>Nothing needs attention</span>
              <span className={s.rowDetail}>All maintenance is up to date.</span>
            </span>
          </div>
        </BaseWidget>
        {taskModal}
      </>
    );
  }

  return (
    <>
      <BaseWidget
        headerTitle="Needs Attention"
        headerIcon={<BiErrorCircle />}
        headerAction={<span className={s.count}>{openTasks.length}</span>}
      >
        <ul className={s.list}>
          {overdueCount > 0 && (
            <li className={s.item}>
              <button
                type="button"
                className={`${s.row} ${s.overdueRow}`}
                onClick={() => navigate(OVERDUE_ROUTE)}
              >
                <span className={`${s.rowIcon} ${s.toneDanger}`} aria-hidden="true">
                  <BiErrorCircle />
                </span>
                <span className={s.rowText}>
                  <span className={s.rowLabel}>
                    {overdueCount} overdue {overdueCount === 1 ? 'item' : 'items'}
                  </span>
                  <span className={s.rowDetail}>Review everything past due</span>
                </span>
                <StatusPill label="Overdue" tone="danger" />
                <BiChevronRight className={s.chevron} aria-hidden="true" />
              </button>
            </li>
          )}

          {visible.map((task) => {
            const { pill, tone, detail } = describe(task, now);
            return (
              <li key={task.id} className={s.item}>
                {/* The row names one job, so it opens that job here — bouncing to the
                    schedule is a detour when the modal is one click away. */}
                <button type="button" className={s.row} onClick={() => setTaskToShowId(task.id)}>
                  <span className={s.rowIcon} aria-hidden="true">
                    <BiWrench />
                  </span>
                  <span className={s.rowText}>
                    <span className={s.rowLabel}>{task.title}</span>
                    <span className={s.rowDetail}>{detail}</span>
                  </span>
                  <StatusPill label={pill} tone={tone} />
                  <BiChevronRight className={s.chevron} aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ul>
      </BaseWidget>
      {taskModal}
    </>
  );
};
